import React, { useState } from 'react';
import { FaTools, FaTrash, FaExclamationTriangle, FaCheck, FaTrashAlt } from 'react-icons/fa';
import PropTypes from 'prop-types';
import axiosInstance from '../utils/axiosConfig';
import { getApiError } from '../utils/apiError';
import { Modal } from './common';
import './DeleteAssetModal.css';

function DeleteTabletModal({ tablet, onClose, onSuccess }) {
  const [selectedState, setSelectedState] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Las bajas exigen motivo: son las acciones que después nadie sabe explicar
  const requiereMotivo = selectedState === 'A_DESINCORPORAR' || selectedState === 'ELIMINAR';

  const handleStateChange = async () => {
    if (!selectedState || (requiereMotivo && !motivo.trim())) return;

    try {
      setLoading(true);
      setError(null);

      // El backend desasigna solo y deja el historial: una sola llamada
      if (selectedState === 'ELIMINAR') {
        await axiosInstance.post(`/api/tablets/${tablet.id}/desincorporar`, { motivo: motivo.trim() });
      } else {
        await axiosInstance.post(`/api/tablets/${tablet.id}/cambiar-estado`, {
          estado: selectedState,
          motivo: motivo.trim()
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      setError(error.response
        ? getApiError(error, 'Error al procesar la solicitud')
        : (error.message || 'Error al procesar la solicitud'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={!!tablet} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2">
          <FaExclamationTriangle className="text-amber-500" />
          Cambiar Estado de la Tablet
        </span>
      </Modal.Header>
      <Modal.Body>
        <p className="modal-description">
          ¿Qué desea hacer con la tablet <strong>{tablet?.marca} {tablet?.modelo}</strong>?
        </p>

        <div className="radio-options">
          <label className="radio-option">
            <input
              type="radio"
              value="DISPONIBLE"
              checked={selectedState === 'DISPONIBLE'}
              onChange={(e) => setSelectedState(e.target.value)}
            />
            <FaCheck className="option-icon" />
            <span>Hacer Disponible</span>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              value="REPARACION"
              checked={selectedState === 'REPARACION'}
              onChange={(e) => setSelectedState(e.target.value)}
            />
            <FaTools className="option-icon" />
            <span>Enviar a Mantenimiento</span>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              value="A_DESINCORPORAR"
              checked={selectedState === 'A_DESINCORPORAR'}
              onChange={(e) => setSelectedState(e.target.value)}
            />
            <FaTrash className="option-icon" />
            <span>Pasar a desincorporar (sigue en el listado mientras se tramita)</span>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              value="ELIMINAR"
              checked={selectedState === 'ELIMINAR'}
              onChange={(e) => setSelectedState(e.target.value)}
            />
            <FaTrashAlt className="option-icon" />
            <span>Desincorporar (sale del listado; conserva el historial)</span>
          </label>
        </div>

        <div className="mt-4">
          <label htmlFor="motivo-tablet" className="block text-sm font-medium text-gray-700 mb-1">
            Motivo{requiereMotivo ? '' : ' (opcional)'}
          </label>
          <textarea
            id="motivo-tablet"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            placeholder={requiereMotivo ? 'Obligatorio: por qué se desincorpora' : 'Por qué se hace el cambio'}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:ring-2 focus:ring-sura-blue focus:border-transparent"
          />
        </div>

        {error && (
          <div className="error-message">
            <FaExclamationTriangle />
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          className="btn btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          className={`btn ${
            selectedState === 'ELIMINAR' ? 'btn-danger' :
            selectedState === 'A_DESINCORPORAR' ? 'bg-amber-500 hover:bg-amber-600 text-white' :
            selectedState === 'REPARACION' ? 'bg-amber-500 hover:bg-amber-600 text-white' :
            'btn-success'
          }`}
          onClick={handleStateChange}
          disabled={!selectedState || loading || (requiereMotivo && !motivo.trim())}
        >
          {loading ? 'Procesando...' : 'Confirmar'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

DeleteTabletModal.propTypes = {
  tablet: PropTypes.shape({
    id: PropTypes.number.isRequired,
    marca: PropTypes.string.isRequired,
    modelo: PropTypes.string.isRequired,
    empleado_id: PropTypes.number
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default DeleteTabletModal;
